/**
 * Formats an object with string key-value pairs into a readable plain text string.
 * Useful for displaying structured data in a user-friendly format.
 *
 * @param obj - Object with string key-value pairs to format
 * @param options - Optional configuration for formatting
 * @returns Formatted plain text string with labeled sections
 *
 * @example
 * const data = {
 *   description: "Software Engineer position",
 *   salary: "$100k - $120k",
 *   benefits: "Health insurance, 401k",
 *   location: "San Francisco, CA"
 * };
 * const formatted = formatObjectToReadableText(data);
 * // Returns plain text with each field displayed with its label
 */
export const formatObjectToReadableText = (
  obj: Record<string, string | null | undefined> | null | undefined,
  options?: {
    /** Custom labels for field names. Key is the field name, value is the label to display */
    fieldLabels?: Record<string, string>;
    /** Fields to exclude from the output */
    excludeFields?: string[];
    /** Separator between sections (default: double line break) */
    sectionSeparator?: string;
    /** Whether to capitalize field names if no label is provided (default: true) */
    capitalizeFieldNames?: boolean;
  },
): string => {
  if (!obj || typeof obj !== 'object') {
    return '';
  }

  const {
    fieldLabels = {},
    excludeFields = [],
    sectionSeparator = '\n\n',
    capitalizeFieldNames = true,
  } = options || {};

  const sections: string[] = [];

  Object.entries(obj).forEach(([key, value]) => {
    // Skip if field is excluded or value is null/undefined/empty
    if (excludeFields.includes(key) || !value || value.trim() === '') {
      return;
    }

    // Get label for the field
    const label =
      fieldLabels[key] ||
      (capitalizeFieldNames
        ? key
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
        : key);

    // Add section with label followed by the value
    sections.push(`${label}:\n${value}`);
  });

  return sections.join(sectionSeparator);
};
